// Ngày giờ nghiệp vụ. Prototype dùng giờ địa phương của trình duyệt; bản production cố định theo
// Asia/Ho_Chi_Minh (00-decisions §3: "ngày công/tháng tính theo Asia/Ho_Chi_Minh") để server, job và
// trình duyệt ở bất kỳ múi giờ nào cho cùng kết quả. Việt Nam dùng UTC+7 cố định từ 1975 (không DST) ⇒
// chỉ cần cộng 7 giờ rồi đọc các thành phần UTC. Chạy prototype dưới TZ=Asia/Ho_Chi_Minh cho kết quả trùng.
import type { DateKey, MonthKey, Timestamp } from './types.js';

export const DAY = 86_400_000;
export const MIN = 60_000;
export const HOUR = 3_600_000;

/** Múi giờ nghiệp vụ duy nhất của hệ thống. */
export const VN_TZ = 'Asia/Ho_Chi_Minh';
/** Độ lệch UTC của giờ Việt Nam (phút). */
export const VN_OFFSET_MIN = 420;
const OFFSET = VN_OFFSET_MIN * MIN;

export const pad2 = (n: number) => String(n).padStart(2, '0');

/** Thành phần lịch của một thời điểm theo giờ Việt Nam. month: 1–12, weekday: 0 = Chủ nhật. */
export interface VnParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}
export function vnParts(t: Timestamp): VnParts {
  const d = new Date(t + OFFSET);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    weekday: d.getUTCDay(),
  };
}

/** Thời điểm (epoch ms) của giờ Việt Nam y-m-d h:mi (month 1–12; tràn tháng/ngày được chuẩn hoá như Date.UTC). */
export const vnTime = (year: number, month: number, day: number, hour = 0, minute = 0, second = 0) =>
  Date.UTC(year, month - 1, day, hour, minute, second) - OFFSET;

/** 'YYYY-MM' theo giờ Việt Nam. */
export function monthKey(t: Timestamp = Date.now()): MonthKey {
  const p = vnParts(t);
  return `${p.year}-${pad2(p.month)}`;
}
/** 'YYYY-MM-DD' theo giờ Việt Nam. */
export function dateKey(t: Timestamp = Date.now()): DateKey {
  const p = vnParts(t);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}
/** Bí danh theo đặc tả attendance-service (`vnDateKey`, `vnMonthKey`, `vnMinutesOfDay`). */
export const vnDateKey = dateKey;
export const vnMonthKey = monthKey;
/** Số phút kể từ 00:00 (giờ Việt Nam) của thời điểm t. */
export function vnMinutesOfDay(t: Timestamp): number {
  const p = vnParts(t);
  return p.hour * 60 + p.minute;
}

// Thiếu thành phần ⇒ NaN (giống prototype: new Date(y, m, undefined) là ngày không hợp lệ).
const parseKey = (k: string) => k.split('-').map(Number) as [number, number, number];

/** "Tháng 10/2026" */
export function monthLabel(mk: MonthKey): string {
  const [y, m] = mk.split('-');
  return `Tháng ${Number(m)}/${y}`;
}
export function shiftMonth(mk: MonthKey, delta: number): MonthKey {
  const [y, m] = parseKey(mk);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`;
}
/** [đầu tháng, đầu tháng sau) theo giờ Việt Nam, dạng epoch ms. */
export function monthRange(mk: MonthKey): [Timestamp, Timestamp] {
  const [y, m] = parseKey(mk);
  return [vnTime(y, m, 1), vnTime(y, m + 1, 1)];
}
export const inMonth = (t: Timestamp | null | undefined, mk: MonthKey) => !!t && monthKey(t) === mk;
/** 00:00 giờ Việt Nam của ngày chứa t. */
export function startOfDay(t: Timestamp = Date.now()): Timestamp {
  const p = vnParts(t);
  return vnTime(p.year, p.month, p.day);
}
/** 00:00 giờ Việt Nam của ngày 'YYYY-MM-DD'. */
export function startOfDateKey(dk: DateKey): Timestamp {
  const [y, m, d] = parseKey(dk);
  return vnTime(y, m, d);
}
/** Thứ trong tuần của ngày lịch 'YYYY-MM-DD' (0 = Chủ nhật). */
export function weekdayOf(dk: DateKey): number {
  const [y, m, d] = parseKey(dk);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/** Số ngày của tháng 'YYYY-MM'. */
export function daysInMonth(mk: MonthKey): number {
  const [y, m] = parseKey(mk);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
/** Số ngày lịch từ a tới b, tính cả hai đầu ('2026-10-05'..'2026-10-06' = 2). */
export function daysBetween(a: DateKey, b: DateKey): number {
  const [y1, m1, d1] = parseKey(a);
  const [y2, m2, d2] = parseKey(b);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DAY) + 1;
}
